import {
	DescriptionRounded,
	DownloadRounded,
	FolderRounded,
	ImageRounded,
	MoreVertRounded,
	OpenInNewRounded,
	SearchRounded,
} from "@mui/icons-material";
import {
	Box,
	CircularProgress,
	IconButton,
	InputAdornment,
	ListItemIcon,
	ListItemText,
	Menu,
	MenuItem,
	TextField,
	Typography,
	alpha,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { useMemo, useRef, useState } from "react";
import { itemTypes } from "../data";

const PRIMARY = "#212121";
const SECONDARY = "#0277bd";
const FOLDER_YELLOW = "#f6a623";

const fileGradient = `linear-gradient(135deg, ${alpha(SECONDARY, 0.7)}, ${SECONDARY})`;

const ArchiveCard = ({ node, onOpen, onDownload, selected }) => {
	const isFolder = node.type === itemTypes.folder;
	const isImage = node.type === itemTypes.image;
	const [anchorEl, setAnchorEl] = useState(null);
	const [isHovered, setIsHovered] = useState(false);
	const menuButtonRef = useRef(null);

	const handleMenuOpen = (e) => {
		e.stopPropagation();
		setAnchorEl(menuButtonRef.current);
	};
	const handleMenuClose = (e) => {
		if (e) e.stopPropagation();
		setAnchorEl(null);
	};
	const handleDownloadClick = (e) => {
		e.stopPropagation();
		handleMenuClose();
		onDownload(node);
	};
	const handlePreviewClick = (e) => {
		e.stopPropagation();
		handleMenuClose();
		if (!isFolder && node.url) window.open(node.url, "_blank", "noopener,noreferrer");
	};

	return (
		<Box
			position="relative"
			onClick={() => onOpen(node)}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				gap: 1,
				p: 1.5,
				borderRadius: "12px",
				cursor: "pointer",
				transition: "all 0.18s ease",
				border: `1.5px solid ${selected ? alpha(PRIMARY, 0.3) : isHovered ? alpha(PRIMARY, 0.1) : "transparent"}`,
				bgcolor: selected ? alpha(PRIMARY, 0.05) : isHovered ? alpha(PRIMARY, 0.03) : "transparent",
				"&:hover": { transform: "translateY(-1px)" },
			}}>
			<Box
				sx={{
					width: 72,
					height: 72,
					borderRadius: "12px",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					overflow: "hidden",
					flexShrink: 0,
					background: isImage && node.url
						? `center / cover no-repeat url(${node.url})`
						: isFolder
						? "transparent"
						: fileGradient,
					boxShadow: isHovered && !isFolder
						? `0 4px 12px ${alpha(PRIMARY, 0.2)}`
						: !isFolder
						? `0 2px 6px ${alpha(PRIMARY, 0.1)}`
						: "none",
					transition: "box-shadow 0.18s ease",
				}}>
				{!(isImage && node.url) && (
					<Box fontSize={isFolder ? 64 : 32} display="flex" color={isFolder ? FOLDER_YELLOW : "white"}>
						{isFolder ? (
							<FolderRounded fontSize="inherit" />
						) : isImage ? (
							<ImageRounded fontSize="inherit" />
						) : (
							<DescriptionRounded fontSize="inherit" />
						)}
					</Box>
				)}
			</Box>

			<Typography
				variant="body2"
				noWrap
				title={node.name}
				sx={{
					maxWidth: 110,
					textAlign: "center",
					fontSize: "0.75rem",
					fontWeight: 500,
					color: selected ? PRIMARY : "text.primary",
				}}>
				{node.name}
			</Typography>

			{isHovered && (
				<IconButton
					ref={menuButtonRef}
					size="small"
					onClick={handleMenuOpen}
					sx={{
						position: "absolute",
						top: 6,
						right: 6,
						width: 24,
						height: 24,
						bgcolor: "white",
						boxShadow: `0 2px 8px ${alpha(PRIMARY, 0.15)}`,
						zIndex: 10,
						"&:hover": { bgcolor: grey[50] },
					}}>
					<MoreVertRounded sx={{ fontSize: 14 }} />
				</IconButton>
			)}

			<Menu
				anchorEl={anchorEl}
				open={Boolean(anchorEl)}
				onClose={handleMenuClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "top", horizontal: "right" }}
				PaperProps={{ sx: { borderRadius: "10px", boxShadow: `0 8px 24px ${alpha(PRIMARY, 0.12)}`, minWidth: 150 } }}>
				{!isFolder && (
					<MenuItem onClick={handlePreviewClick} sx={{ fontSize: "0.82rem", borderRadius: "6px", mx: 0.5 }}>
						<ListItemIcon sx={{ minWidth: 30 }}>
							<OpenInNewRounded fontSize="small" />
						</ListItemIcon>
						<ListItemText primaryTypographyProps={{ fontSize: "0.82rem" }}>Preview</ListItemText>
					</MenuItem>
				)}
				<MenuItem onClick={handleDownloadClick} sx={{ fontSize: "0.82rem", borderRadius: "6px", mx: 0.5 }}>
					<ListItemIcon sx={{ minWidth: 30 }}>
						<DownloadRounded fontSize="small" />
					</ListItemIcon>
					<ListItemText primaryTypographyProps={{ fontSize: "0.82rem" }}>Download</ListItemText>
				</MenuItem>
			</Menu>
		</Box>
	);
};

const ArchiveGrid = ({ items, loading, selectedNodePath, onOpen, onDownload }) => {
	const [searchQuery, setSearchQuery] = useState("");

	const filteredItems = useMemo(() => {
		if (!searchQuery.trim()) return items;
		return items.filter((item) => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
	}, [items, searchQuery]);

	return (
		<Box display="flex" flexDirection="column" flex={1} overflow="hidden">
			<Box px={2.5} py={1.5} borderBottom={`1px solid ${alpha(grey[900], 0.06)}`}>
				<TextField
					fullWidth
					size="small"
					placeholder="Search files and folders..."
					value={searchQuery}
					onChange={(e) => setSearchQuery(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchRounded sx={{ fontSize: 18, color: grey[400] }} />
							</InputAdornment>
						),
						sx: { borderRadius: "10px", fontSize: "0.85rem", bgcolor: alpha(grey[900], 0.03) },
					}}
					sx={{ "& .MuiOutlinedInput-root": { "& fieldset": { borderColor: alpha(grey[900], 0.12) } } }}
				/>
			</Box>

			<Box p={2.5} flex={1} overflow="auto">
				{loading ? (
					<Box height="100%" display="flex" alignItems="center" justifyContent="center" columnGap={1.5}>
						<CircularProgress size={20} thickness={4} sx={{ color: PRIMARY }} />
						<Typography variant="body2" color="text.secondary">
							Loading archive...
						</Typography>
					</Box>
				) : filteredItems.length ? (
					<Box display="grid" gridTemplateColumns="repeat(auto-fill, minmax(130px, 1fr))" gap={0.5}>
						{filteredItems.map((node) => (
							<ArchiveCard
								key={node.path}
								node={node}
								selected={selectedNodePath === node.path}
								onOpen={onOpen}
								onDownload={onDownload}
							/>
						))}
					</Box>
				) : (
					<Box height="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center" gap={1}>
						<Box
							sx={{
								width: 56,
								height: 56,
								borderRadius: "16px",
								bgcolor: alpha(PRIMARY, 0.05),
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							<SearchRounded sx={{ fontSize: 28, color: grey[400] }} />
						</Box>
						<Typography variant="body2" color="text.disabled" fontSize="0.82rem">
							{searchQuery ? `No results for "${searchQuery}"` : "No files found at this level."}
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default ArchiveGrid;
